/**
 * Cell Renderers Index
 * 
 * Maps Parse field types to their respective cell renderer components.
 * Each renderer handles the display of a specific Parse data type with:
 * - Truncation for long values
 * - Click-to-expand for complex data
 * - Type-appropriate formatting
 */

import { ParseFieldType, CellRendererProps } from '../../../../../types/AppPanel';
import StringCell from './StringCell';
import NumberCell from './NumberCell';
import BooleanCell from './BooleanCell';
import DateCell from './DateCell';
import PointerCell from './PointerCell';
import RelationCell from './RelationCell';
import ArrayCell from './ArrayCell';
import ObjectCell from './ObjectCell';
import FileCell from './FileCell';
import GeoPointCell from './GeoPointCell';
import PolygonCell from './PolygonCell';
import ACLCell from './ACLCell';
import BytesCell from './BytesCell';

// Type for cell renderer component - uses unknown to be compatible with all cell types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CellRenderer = React.FC<CellRendererProps<any>>;

// Map of Parse field types to their cell renderers
const cellRenderers: Record<ParseFieldType, CellRenderer> = {
  String: StringCell as CellRenderer,
  Number: NumberCell as CellRenderer,
  Boolean: BooleanCell as CellRenderer,
  Date: DateCell as CellRenderer,
  Pointer: PointerCell as CellRenderer,
  Relation: RelationCell as CellRenderer,
  Array: ArrayCell as CellRenderer,
  Object: ObjectCell as CellRenderer,
  File: FileCell as CellRenderer,
  GeoPoint: GeoPointCell as CellRenderer,
  Polygon: PolygonCell as CellRenderer,
  ACL: ACLCell as CellRenderer,
  Bytes: BytesCell as CellRenderer,
};

/**
 * Get the appropriate cell renderer for a Parse field type
 * Falls back to StringCell for unknown types
 */
export function getCellRenderer(type: ParseFieldType): CellRenderer {
  return cellRenderers[type] || StringCell;
}

// Export all cell renderers for direct use
export {
  StringCell,
  NumberCell,
  BooleanCell,
  DateCell,
  PointerCell,
  RelationCell,
  ArrayCell,
  ObjectCell,
  FileCell,
  GeoPointCell,
  PolygonCell,
  ACLCell,
  BytesCell,
};

